import type { Voice } from '@listening/api-contract/legacy-client-types';
import { PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES } from '@listening/api-contract';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import { VoiceModelId } from '@listening/api-contract/schemas';
import { clientEnv } from '@/env';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@listening/frontend/components/radix/popover';
import { useMutation, useQuery, queryOptions } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { Clock, Data, Effect, Option, Predicate, Schema } from 'effect';
import { HttpClient, HttpClientRequest } from 'effect/unstable/http';
import {
  Code,
  Download,
  Page,
  Refresh,
  RefreshDouble,
  Upload,
} from 'iconoir-react';
import {
  NavArrowDown as NavArrowDownSolid,
  WarningCircle as WarningCircleSolid,
} from 'iconoir-react/solid';
import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type AudioExportFormat,
  downloadBlob,
  exportAudioAs,
} from '@/lib/audio-format-export';
import { PublicToolAudioPlayer } from '@/components/astro/new-design-25/SEO/public-tool-audio-player';
import { PublicToolReadAlong } from '@/components/astro/new-design-25/SEO/public-tool-read-along';
import {
  navigateToPublicToolReadAlong,
  waitForPublicToolJob,
  waitForPublicToolReadAlong,
} from '@/lib/public-tool-job';
import { prewarmTurnstileToken } from '@/lib/turnstile';
import {
  catchPublicToolHttpError,
  takeTurnstileTokenEffect,
} from '@/lib/public-tool-errors';
import { capturePublicToolClientTiming } from '@/lib/public-tool-client-timing';
import { voiceLangToLangLabel } from '@/lib/utils';
import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { eq } from '@/runtimes/effect-query';
import { callApiPromise } from '@/runtimes/get-runtime';
import { randomUUID } from '@listening/utils/crypto';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'pdf-to-audio-converter' });

const EXPORT_FORMATS: ReadonlyArray<{
  value: AudioExportFormat;
  label: string;
}> = [
  { value: 'mp3', label: 'MP3' },
  { value: 'wav', label: 'WAV' },
  { value: 'ogg', label: 'OGG' },
  { value: 'flac', label: 'FLAC' },
  { value: 'm4a', label: 'M4A' },
  { value: 'aac', label: 'AAC' },
];

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5] as const;
const PDF_JOB_MAX_POLLS = 240;
const PDF_JOB_QUERY_PARAM = 'pdf_job';
const PDF_UPLOAD_QUERY_PARAM = 'pdf_upload';
const PDF_STORAGE_PREFIX = 'public-pdf-to-audio';
const PUBLIC_DOCUMENT_ACCEPT =
  'application/pdf,text/markdown,text/x-markdown,text/mdx,.pdf,.md,.markdown,.mdx';
type Speed = (typeof SPEED_OPTIONS)[number];
type PublicUploadDocumentType = 'pdf' | 'md';

// Client-side failures of the document pipeline, carried in the mutation's
// typed error channel next to the contract's own errors (BadRequestError,
// CaptchaError, RateLimitedError, InternalServerError). Distinct tags so the
// alert picks copy with `error.match` rather than rendering a raw message.
class PdfNoFileSelectedError extends Data.TaggedError(
  'PdfNoFileSelectedError',
) {}
class PdfUploadFailedError extends Data.TaggedError('PdfUploadFailedError') {}
class PdfUnexpectedToolError extends Data.TaggedError(
  'PdfUnexpectedToolError',
) {}
class PdfJobFailedError extends Data.TaggedError('PdfJobFailedError') {}

const PDF_GENERATION_FAILED_COPY =
  'Could not generate audio from this document. Please try again later.';

// Anything the matcher does not name: an unknown failure tag or a defect.
const PDF_UNEXPECTED_FAILURE_COPY =
  'Could not generate audio from this document. Please try another file.';

function describeVoice(voice: Voice) {
  const gender =
    voice.gender === 'male'
      ? m.settings_voice_male()
      : m.settings_voice_female();
  const softness =
    voice.softness === 'soft'
      ? m.settings_voice_soft()
      : voice.softness === 'deep'
        ? m.settings_voice_deep()
        : m.settings_voice_moderate();
  const language =
    voice.lang === 'en' ? null : voiceLangToLangLabel(voice.lang);
  return [gender, softness, language].filter(Boolean).join(' · ');
}

function pickInitialVoice({
  preferredGender,
  preferredLang,
  voices,
}: {
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  voices: ReadonlyArray<Voice>;
}): Voice | undefined {
  const locale = getLocale();
  return (
    voices.find(
      (v) =>
        v.lang === (preferredLang ?? locale) &&
        (!preferredGender || v.gender === preferredGender),
    ) ??
    voices.find((v) => v.lang === (preferredLang ?? locale)) ??
    voices.find((v) => !preferredGender || v.gender === preferredGender) ??
    voices[0]
  );
}

type PdfProcessStage = 'uploading' | 'submitting' | 'pending' | 'processing';
const StoredPdfJob = Schema.Struct({
  fileName: Schema.String,
  documentType: Schema.optional(Schema.Literals(['pdf', 'md'])),
  fileSizeBytes: Schema.Finite,
  startedAtMs: Schema.Finite,
  voice: Schema.optional(VoiceModelId),
  uploadId: Schema.optional(Schema.String),
  documentUrl: Schema.optional(Schema.String),
  jobId: Schema.optional(Schema.String),
  stage: Schema.optional(
    Schema.Literals(['uploading', 'submitting', 'pending', 'processing']),
  ),
});
const decodeStoredPdfJob = Schema.decodeUnknownOption(StoredPdfJob);

type PdfResult = Awaited<ReturnType<typeof waitForPublicToolJob>>;

type PdfJob = {
  fileName: string;
  documentType?: PublicUploadDocumentType | undefined;
  fileSizeBytes: number;
  startedAtMs: number;
  file?: File | undefined;
  voice?: Voice['model_id'] | undefined;
  uploadId?: string | undefined;
  documentUrl?: string | undefined;
  jobId?: string | undefined;
  stage?: PdfProcessStage | undefined;
  result?: PdfResult | undefined;
};

type PdfGeneratePayload = {
  file?: File | undefined;
  documentUrl?: string | undefined;
  voice?: Voice['model_id'] | undefined;
  fileSizeBytes: number;
  fileName: string;
  documentType: PublicUploadDocumentType;
  startedAtMs?: number | undefined;
};

/**
 * The job poll runs over `GetJob`, whose own failures (job failed, poll
 * timeout, transport error) arrive as untyped rejections. Collapse them into
 * one tag — the rejection message can carry the request URL, so it is logged
 * rather than shown.
 */
const waitForJob = <A,>(label: string, wait: () => Promise<A>) =>
  Effect.tryPromise({
    try: wait,
    catch: (cause) => {
      moduleLogger.error({ err: cause }, `[Listening] ${label} failed`);
      return new PdfJobFailedError();
    },
  });

function requireSelectedPdfFile(file: File | undefined) {
  return file
    ? Effect.succeed(file)
    : Effect.fail(new PdfNoFileSelectedError());
}

function makePdfUploadFormData(
  fields: Record<string, string>,
  file: File,
): FormData {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);
  return formData;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toString()} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getPublicUploadDocumentTypeFromName(
  filename: string,
): PublicUploadDocumentType | null {
  const lower = filename.trim().toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (
    lower.endsWith('.md') ||
    lower.endsWith('.markdown') ||
    lower.endsWith('.mdx')
  )
    return 'md';
  return null;
}

function getPublicUploadDocumentType(
  file: File,
): PublicUploadDocumentType | null {
  const documentTypeFromName = getPublicUploadDocumentTypeFromName(file.name);
  if (documentTypeFromName) return documentTypeFromName;

  const contentType = file.type.split(';')[0]?.trim().toLowerCase();
  if (contentType === 'application/pdf') return 'pdf';
  if (
    contentType === 'text/markdown' ||
    contentType === 'text/x-markdown' ||
    contentType === 'text/mdx'
  )
    return 'md';
  return null;
}

function getDocumentTypeLabel(documentType?: PublicUploadDocumentType) {
  return documentType === 'md' ? 'Markdown' : 'PDF';
}

function getFileBaseName(filename: string) {
  return filename.replace(/\.(pdf|md|markdown|mdx)$/i, '');
}

function estimatePdfConversionSeconds(bytes: number) {
  const mb = bytes / 1024 / 1024;
  const min = Math.round(Math.min(240, Math.max(75, 70 + mb * 12)));
  const max = Math.round(Math.min(420, Math.max(min + 45, 120 + mb * 18)));
  return { min, max };
}

function formatMinutes(seconds: number) {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes.toString()} min`;
}

function formatEstimate(bytes: number) {
  const estimate = estimatePdfConversionSeconds(bytes);
  return `${formatMinutes(estimate.min)}-${formatMinutes(estimate.max)}`;
}

const PDF_STAGE_LABELS = {
  uploading: 'Uploading document',
  submitting: 'Starting conversion',
  pending: 'Queued for conversion',
  processing: 'Converting document to audio',
} satisfies Record<PdfProcessStage, string>;

const PDF_STAGE_PROGRESS = {
  uploading: 18,
  submitting: 35,
  pending: 50,
  processing: 62,
} satisfies Record<PdfProcessStage, number>;

const PDF_STAGE_ORDER = {
  uploading: 0,
  submitting: 1,
  pending: 2,
  processing: 3,
} satisfies Record<PdfProcessStage, number>;

function getStageLabel(stage: PdfProcessStage) {
  return PDF_STAGE_LABELS[stage];
}

function getLaterStage(
  current: PdfProcessStage | undefined,
  next: PdfProcessStage,
) {
  return current && PDF_STAGE_ORDER[current] > PDF_STAGE_ORDER[next]
    ? current
    : next;
}

function isPdfProcessStage(status: string): status is PdfProcessStage {
  return status in PDF_STAGE_ORDER;
}

function getProgressPercent({
  fileSizeBytes,
  startedAtMs,
  nowMs,
  stage,
}: {
  fileSizeBytes: number;
  startedAtMs: number;
  nowMs: number;
  stage: PdfProcessStage;
}) {
  if (stage !== 'processing') return PDF_STAGE_PROGRESS[stage];

  const estimate = estimatePdfConversionSeconds(fileSizeBytes);
  const elapsedSeconds = Math.max(0, (nowMs - startedAtMs) / 1000);
  const baseProgress = PDF_STAGE_PROGRESS.processing;
  const progressRange = 95 - baseProgress;
  return Math.min(
    95,
    Math.max(
      baseProgress,
      Math.round(
        baseProgress + (elapsedSeconds / estimate.max) * progressRange,
      ),
    ),
  );
}

function setPdfJobStage(
  setPdfJob: Dispatch<SetStateAction<PdfJob | null>>,
  stage: PdfProcessStage,
) {
  setPdfJob((job) =>
    job ? { ...job, stage: getLaterStage(job.stage, stage) } : job,
  );
}

function syncStageFromJobStatus(
  status: { status: string },
  setPdfJob: Dispatch<SetStateAction<PdfJob | null>>,
) {
  if (!isPdfProcessStage(status.status)) return;
  const stage = status.status;
  setPdfJob((job) =>
    job?.jobId ? { ...job, stage: getLaterStage(job.stage, stage) } : job,
  );
}

function readStoredPdfJob(key: string): PdfJob | null {
  try {
    const value = window.sessionStorage.getItem(key);
    if (!value) return null;
    return Option.getOrNull(decodeStoredPdfJob(JSON.parse(value)));
  } catch {
    return null;
  }
}

function writeStoredJson(key: string, value: unknown) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Resume state is best-effort. Conversion should continue if storage is blocked.
  }
}

function jobStorageKey(jobId: string) {
  return `${PDF_STORAGE_PREFIX}:job:${jobId}`;
}

function uploadStorageKey(id: string) {
  return `${PDF_STORAGE_PREFIX}:upload:${id}`;
}

type PdfUrlState =
  | { type: 'job'; id: string }
  | { type: 'upload'; id: string }
  | { type: 'clear' };

function updatePdfUrlState(state: PdfUrlState) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete(PDF_JOB_QUERY_PARAM);
  url.searchParams.delete(PDF_UPLOAD_QUERY_PARAM);
  if (state.type === 'job') url.searchParams.set(PDF_JOB_QUERY_PARAM, state.id);
  if (state.type === 'upload')
    url.searchParams.set(PDF_UPLOAD_QUERY_PARAM, state.id);
  window.history.replaceState(window.history.state, '', url.toString());
}

function readPdfResumeIds() {
  if (typeof window === 'undefined') return { jobId: null, uploadId: null };
  const params = new URLSearchParams(window.location.search);
  return {
    jobId: params.get(PDF_JOB_QUERY_PARAM),
    uploadId: params.get(PDF_UPLOAD_QUERY_PARAM),
  };
}

export function PdfToAudioConverter({
  heading = 'PDF to audio',
  preferredGender,
  preferredLang,
  subtitle = 'Drop a PDF, pick a natural AI voice, and listen to it as audio.',
}: {
  heading?: string | undefined;
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  subtitle?: string | undefined;
}) {
  const voicesQuery = useQuery(
    queryOptions({
      queryKey: ['tts-tool', 'voices'],
      queryFn: () => callApiPromise((api) => api.Voices.ListVoices()),
      select: (response) => response.voices.filter((v) => v.is_free),
      staleTime: 1000 * 60 * 60 * 24,
    }),
  );
  const voices = voicesQuery.data ?? [];

  const [voiceOverride, setVoiceOverride] = useState<Voice['model_id']>();
  const voiceModelId =
    voiceOverride ??
    pickInitialVoice({ preferredGender, preferredLang, voices })?.model_id;
  const selectedVoice = voices.find((v) => v.model_id === voiceModelId);

  const [pdfJob, setPdfJob] = useState<PdfJob | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [format, setFormat] = useState<AudioExportFormat>('mp3');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isResultRedirectRef = useRef(false);
  const resumedIdRef = useRef<string | null>(null);
  const activeVoice = pdfJob?.voice ?? voiceModelId;

  const navigate = useNavigate();
  const redirectToReadAlong = useCallback(
    (jobId: string, prefs: { speed: Speed; voice: Voice['model_id'] }) => {
      isResultRedirectRef.current = true;
      void navigateToPublicToolReadAlong(navigate, jobId, {
        speed: prefs.speed,
        voice: prefs.voice,
      });
    },
    [navigate],
  );

  const generate = useMutation({
    ...eq.mutationOptions({
      mutationFn: (payload: PdfGeneratePayload) =>
        Effect.gen(function* () {
          const api = yield* ApiClient;
          const startedAtMs =
            payload.startedAtMs ?? (yield* Clock.currentTimeMillis);
          const baseJob: PdfJob = {
            fileName: payload.fileName,
            documentType: payload.documentType,
            fileSizeBytes: payload.fileSizeBytes,
            startedAtMs,
            voice: payload.voice,
          };
          let documentUrl = payload.documentUrl;

          if (!documentUrl) {
            const file = yield* requireSelectedPdfFile(payload.file);
            yield* Effect.sync(() =>
              setPdfJob({ ...baseJob, file, stage: 'uploading' }),
            );

            const uploadToken = yield* takeTurnstileTokenEffect(
              clientEnv.turnstileSiteKey,
            );
            // The submit step needs its own token (single-use) — warm it while
            // the upload is in flight.
            yield* Effect.sync(() =>
              prewarmTurnstileToken({ sitekey: clientEnv.turnstileSiteKey }),
            );
            const upload = yield* api.PublicTools.PdfUploadUrl({
              payload: {
                filename: payload.fileName,
                fileSizeBytes: payload.fileSizeBytes,
                cf_turnstile_token: uploadToken,
              },
            }).pipe(
              Effect.catchTag(
                'HttpClientError',
                catchPublicToolHttpError('pdf-to-audio'),
              ),
            );

            documentUrl = upload.document_url;
            const uploadId = yield* randomUUID;
            const uploadedJob: PdfJob = {
              ...baseJob,
              file,
              uploadId,
              documentUrl,
              stage: 'uploading',
            };
            yield* Effect.sync(() => setPdfJob(uploadedJob));

            const revertToSelected = Effect.sync(() => {
              setPdfJob({ ...baseJob, file });
              updatePdfUrlState({ type: 'clear' });
            });

            const http = yield* HttpClient.HttpClient;
            const uploadResponse = yield* HttpClientRequest.post(
              upload.signed_upload_url,
            ).pipe(
              HttpClientRequest.bodyFormData(
                makePdfUploadFormData(upload.fields, file),
              ),
              http.execute,
              Effect.scoped,
              Effect.mapError(() => new PdfUploadFailedError()),
              Effect.catch((error) =>
                revertToSelected.pipe(Effect.andThen(error)),
              ),
            );
            if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
              yield* revertToSelected;
              return yield* new PdfUploadFailedError();
            }
            yield* Effect.sync(() => {
              writeStoredJson(uploadStorageKey(uploadId), uploadedJob);
              updatePdfUrlState({ type: 'upload', id: uploadId });
            });
          }

          yield* Effect.sync(() => setPdfJobStage(setPdfJob, 'submitting'));
          const submitStart = performance.now();
          const submitToken = yield* takeTurnstileTokenEffect(
            clientEnv.turnstileSiteKey,
          );
          const turnstileDoneAt = performance.now();
          const job = yield* api.PublicTools.PdfToAudio({
            payload: {
              document_url: documentUrl,
              fileName: payload.fileName,
              fileSizeBytes: payload.fileSizeBytes,
              voice: payload.voice,
              cf_turnstile_token: submitToken,
            },
          }).pipe(
            Effect.catchTag(
              'HttpClientError',
              catchPublicToolHttpError('pdf-to-audio'),
            ),
          );
          const runningJob: PdfJob = {
            ...baseJob,
            documentUrl,
            jobId: job.job_id,
            stage: 'pending',
          };
          yield* Effect.sync(() => {
            writeStoredJson(jobStorageKey(job.job_id), runningJob);
            updatePdfUrlState({ type: 'job', id: job.job_id });
            setPdfJob(runningJob);
          });
          // Resolves as soon as the read-along is openable (conversion done,
          // TTS still synthesizing) instead of waiting for the full pipeline.
          const jobCreatedAt = performance.now();
          const ready = yield* waitForJob('pdf-to-audio read-along', () =>
            waitForPublicToolReadAlong(job.job_id, {
              maxPolls: PDF_JOB_MAX_POLLS,
              onStatus: (status) => syncStageFromJobStatus(status, setPdfJob),
            }),
          );
          yield* Effect.sync(() =>
            capturePublicToolClientTiming({
              tool: 'pdf-to-audio',
              turnstileMs: turnstileDoneAt - submitStart,
              createJobMs: jobCreatedAt - turnstileDoneAt,
              waitReadyMs: performance.now() - jobCreatedAt,
              readyFromPartial: !ready.completed,
            }),
          );
          if (ready.tool !== 'pdf-to-audio') {
            return yield* new PdfUnexpectedToolError();
          }
          const audioId = ready.audio_id;
          const redirectVoice = payload.voice;
          if (audioId && redirectVoice) {
            yield* Effect.sync(() =>
              redirectToReadAlong(audioId, {
                speed,
                voice: redirectVoice,
              }),
            );
            return null;
          }
          // No voice to carry into the reader — fall back to waiting for the
          // completed result and rendering the inline player.
          const response = yield* waitForJob('pdf-to-audio job', () =>
            waitForPublicToolJob(job.job_id, {
              maxPolls: PDF_JOB_MAX_POLLS,
              onStatus: (status) => syncStageFromJobStatus(status, setPdfJob),
            }),
          );
          if (response.tool !== 'pdf-to-audio') {
            return yield* new PdfUnexpectedToolError();
          }
          yield* Effect.sync(() =>
            setPdfJob((current) =>
              current
                ? { ...current, result: response, stage: undefined }
                : current,
            ),
          );
          return response;
        }).pipe(
          Effect.ensuring(
            Effect.sync(() =>
              setPdfJob((job) =>
                job && !job.result ? { ...job, stage: undefined } : job,
              ),
            ),
          ),
        ),
    }),
    onError: (error) => {
      if (Predicate.isTagged(error, 'EffectQueryDefect')) {
        moduleLogger.error(
          { defectCause: error.defectCause },
          '[Listening] pdf-to-audio defect',
        );
      }
    },
  });
  const { mutate: generateMutate } = generate;

  const download = useMutation({
    mutationFn: async (input: {
      url: string;
      format: AudioExportFormat;
      speed: Speed;
    }) => {
      const blob = await exportAudioAs(input.url, input.format, {
        playbackRate: input.speed,
      });
      const baseName = pdfJob
        ? getFileBaseName(pdfJob.fileName)
        : 'listening-pdf-to-audio';
      const speedSuffix = input.speed === 1 ? '' : `-${input.speed}x`;
      downloadBlob(blob, `${baseName}${speedSuffix}.${input.format}`);
    },
  });

  const result = pdfJob?.result ?? null;
  const visibleStage =
    pdfJob?.stage ?? (generate.isPending ? 'submitting' : null);
  const isWorking = generate.isPending || visibleStage !== null;
  const canGeneratePdf =
    Boolean(pdfJob) && Boolean(activeVoice) && !generate.isPending;
  const progressPercent =
    pdfJob && visibleStage
      ? getProgressPercent({
          fileSizeBytes: pdfJob.fileSizeBytes,
          startedAtMs: pdfJob.startedAtMs,
          nowMs,
          stage: visibleStage,
        })
      : 0;
  // A defect (network/parse) carries no tag, so it lands in `OrElse`; `onError`
  // logs it. Never render its `.message` — that's `Cause.pretty(cause)`.
  const generateErrorMessage = generate.error
    ? generate.error.match({
        PublicToolCaptchaError: () => m.try_magic_error_captcha(),
        CaptchaError: () => m.try_magic_error_captcha(),
        // Server-authored validation copy (upload rejected, tools disabled, ...).
        BadRequestError: (error) => error.message,
        RateLimitedError: (error) =>
          error.scope === 'global'
            ? error.message
            : m.try_magic_error_rate_limit(),
        PublicToolRateLimitedError: () => m.try_magic_error_rate_limit(),
        PublicToolUnavailableError: () =>
          'Document to audio is not available in this environment yet.',
        PublicToolRequestFailedError: () => PDF_GENERATION_FAILED_COPY,
        InternalServerError: () => PDF_GENERATION_FAILED_COPY,
        PdfNoFileSelectedError: () => 'Please choose a PDF or Markdown file.',
        PdfUploadFailedError: () =>
          'Could not upload this document. Please try again.',
        PdfUnexpectedToolError: () => 'Unexpected public tool response',
        PdfJobFailedError: () => PDF_GENERATION_FAILED_COPY,
        OrElse: () => PDF_UNEXPECTED_FAILURE_COPY,
      })
    : null;
  const errorMessage =
    generateErrorMessage ??
    (download.isError ? m.tts_tool_download_error() : fileError);

  useEffect(() => {
    if (!isWorking) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isWorking]);

  useEffect(() => {
    if (pdfJob) return;

    const { jobId, uploadId } = readPdfResumeIds();
    if (jobId) {
      if (resumedIdRef.current === jobId) return;
      resumedIdRef.current = jobId;
      const stored = readStoredPdfJob(jobStorageKey(jobId)) ?? {
        fileName: 'Uploaded document',
        fileSizeBytes: 0,
        startedAtMs: Date.now(),
      };
      const resumed: PdfJob = { ...stored, jobId, stage: 'pending' };
      setPdfJob(resumed);
      const resumeJob = async () => {
        const ready = await waitForPublicToolReadAlong(jobId, {
          maxPolls: PDF_JOB_MAX_POLLS,
          onStatus: (status) => syncStageFromJobStatus(status, setPdfJob),
        });
        if (ready.tool !== 'pdf-to-audio') {
          throw new Error('Unexpected public tool response');
        }
        if (ready.audio_id) {
          const redirectVoice = stored.voice ?? voiceModelId;
          if (redirectVoice) {
            redirectToReadAlong(ready.audio_id, {
              speed,
              voice: redirectVoice,
            });
            return;
          }
        }
        // No voice to carry into the reader — wait for the completed
        // result and render the inline player instead.
        const response = await waitForPublicToolJob(jobId, {
          maxPolls: PDF_JOB_MAX_POLLS,
          onStatus: (status) => syncStageFromJobStatus(status, setPdfJob),
        });
        if (response.tool !== 'pdf-to-audio') {
          throw new Error('Unexpected public tool response');
        }
        setPdfJob((current) =>
          current
            ? { ...current, result: response, stage: undefined }
            : current,
        );
      };
      void resumeJob().catch((error: unknown) => {
        // The poll rejects with untyped values (job failure, timeout, transport
        // error) whose messages can carry the request URL — log, show fixed copy.
        moduleLogger.error(
          { err: error },
          '[Listening] pdf-to-audio resume failed',
        );
        setFileError(PDF_GENERATION_FAILED_COPY);
        setPdfJob((current) =>
          current && !current.result
            ? { ...current, stage: undefined }
            : current,
        );
      });
      return;
    }

    if (!uploadId) return;
    const stored = readStoredPdfJob(uploadStorageKey(uploadId));
    if (!stored || resumedIdRef.current === uploadId) return;
    resumedIdRef.current = uploadId;
    setPdfJob({ ...stored, stage: 'submitting' });
    const documentType =
      stored.documentType ??
      getPublicUploadDocumentTypeFromName(stored.fileName) ??
      'pdf';
    generateMutate({
      documentUrl: stored.documentUrl,
      documentType,
      voice: stored.voice ?? voiceModelId,
      fileName: stored.fileName,
      fileSizeBytes: stored.fileSizeBytes,
      startedAtMs: stored.startedAtMs,
    });
  }, [generateMutate, pdfJob, redirectToReadAlong, speed, voiceModelId]);

  useEffect(() => {
    if (!isWorking) return;
    const handler = (event: BeforeUnloadEvent) => {
      if (isResultRedirectRef.current) return;
      event.preventDefault();
      // oxlint-disable-next-line typescript/no-deprecated -- Chromium < 119 only prompts when returnValue is set
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isWorking]);

  const blocker = useBlocker({
    shouldBlockFn: () => isWorking && !isResultRedirectRef.current,
    withResolver: true,
  });

  const handleFile = (file: File) => {
    setFileError(null);
    generate.reset();
    setPdfJob(null);
    updatePdfUrlState({ type: 'clear' });
    // Warm the CAPTCHA token while the user reviews their selection — the
    // upload step consumes it, keeping the round-trip off the critical path.
    prewarmTurnstileToken({ sitekey: clientEnv.turnstileSiteKey });
    const documentType = getPublicUploadDocumentType(file);
    if (!documentType) {
      setFileError('Please choose a PDF or Markdown file.');
      return;
    }
    if (file.size > PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES) {
      setFileError(
        `Document is larger than the ${Math.floor(
          PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES / 1024 / 1024,
        ).toString()} MB limit.`,
      );
      return;
    }
    setPdfJob({
      file,
      fileName: file.name,
      documentType,
      fileSizeBytes: file.size,
      startedAtMs: Date.now(),
    });
  };

  const onPickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onSubmit = () => {
    if (!pdfJob || generate.isPending) return;
    if (!activeVoice) return;
    generate.mutate({
      file: pdfJob.file,
      documentUrl: pdfJob.documentUrl,
      documentType:
        pdfJob.documentType ??
        getPublicUploadDocumentTypeFromName(pdfJob.fileName) ??
        'pdf',
      voice: activeVoice,
      fileName: pdfJob.fileName,
      fileSizeBytes: pdfJob.fileSizeBytes,
      startedAtMs: pdfJob.startedAtMs,
    });
  };

  const resetTool = () => {
    setPdfJob(null);
    setFileError(null);
    generate.reset();
    download.reset();
    updatePdfUrlState({ type: 'clear' });
  };

  return (
    <section
      aria-labelledby='pdf-to-audio-tool-heading'
      className='mx-auto max-w-6xl px-4 pt-10 pb-16 sm:px-6 sm:pt-14 lg:px-8'
    >
      <Dialog
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
        open={blocker.status === 'blocked'}
      >
        <DialogContent className='max-w-sm rounded-2xl border border-navy-220/10 bg-white p-6'>
          <DialogHeader>
            <DialogTitle className='text-base font-semibold text-navy-220'>
              Leave while your document is converting?
            </DialogTitle>
            <DialogDescription className='mt-2 text-sm leading-6 text-navy-220/65'>
              Your conversion is still running. Once the job starts, this URL
              can recover it, but staying here is the safest way to keep
              progress visible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='mt-4 flex gap-2 sm:gap-2'>
            <button
              className='flex-1 rounded-full border border-navy-220/15 px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5'
              onClick={() => blocker.reset?.()}
              type='button'
            >
              Stay
            </button>
            <button
              className='flex-1 rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-600'
              onClick={() => blocker.proceed?.()}
              type='button'
            >
              Leave
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className='mb-8 text-center sm:mb-10'>
        <h1
          className='text-3xl font-semibold tracking-[-0.04em] text-navy-220 sm:text-5xl'
          id='pdf-to-audio-tool-heading'
        >
          {heading}
        </h1>
        <p className='mx-auto mt-3 max-w-2xl text-base leading-7 text-navy-220/65 sm:text-lg'>
          {subtitle}
        </p>
      </div>

      <div className='rounded-[28px] border border-navy-220/10 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]'>
        {!pdfJob && (
          <div className='p-6 sm:p-10'>
            <button
              className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition ${
                isDragging
                  ? 'border-royal-50 bg-royal-50/5'
                  : 'border-navy-220/15 bg-navy-220/[0.02] hover:border-navy-220/30'
              }`}
              disabled={generate.isPending}
              onClick={() => fileInputRef.current?.click()}
              onDragLeave={() => setIsDragging(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDrop={onDrop}
              type='button'
            >
              {generate.isPending ? (
                <RefreshDouble
                  aria-hidden='true'
                  className='size-10 animate-spin text-royal-50'
                />
              ) : (
                <Upload
                  aria-hidden='true'
                  className='size-10 text-navy-220/50'
                />
              )}
              <span className='text-lg font-semibold text-navy-220'>
                {generate.isPending
                  ? 'Preparing your document…'
                  : 'Drop a PDF or Markdown file'}
              </span>
              <span className='text-sm text-navy-220/60'>
                PDF and Markdown files up to{' '}
                {Math.floor(
                  PUBLIC_TOOL_PDF_MAX_FILE_SIZE_BYTES / 1024 / 1024,
                ).toString()}{' '}
                MB · processed with Listening’s reader pipeline
              </span>
            </button>
            <input
              accept={PUBLIC_DOCUMENT_ACCEPT}
              className='hidden'
              onChange={onPickFile}
              ref={fileInputRef}
              type='file'
            />
          </div>
        )}

        {pdfJob && !result && (
          <div className='p-6 sm:p-10'>
            <div className='flex items-start gap-4 rounded-2xl border border-navy-220/10 bg-navy-220/[0.02] p-4 sm:p-6'>
              <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-royal-50/10 text-royal-50'>
                {pdfJob.documentType === 'md' ? (
                  <Code aria-hidden='true' className='size-6' />
                ) : (
                  <Page aria-hidden='true' className='size-6' />
                )}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-base font-semibold text-navy-220'>
                  {pdfJob.fileName}
                </p>
                <p className='mt-1 text-sm text-navy-220/60'>
                  {formatBytes(pdfJob.fileSizeBytes)} ·{' '}
                  {getDocumentTypeLabel(pdfJob.documentType)} conversion
                </p>
              </div>
              <button
                aria-label='Choose a different file'
                className='inline-flex size-9 items-center justify-center rounded-full text-navy-220/60 transition hover:bg-navy-220/5 hover:text-navy-220'
                onClick={resetTool}
                type='button'
              >
                <Refresh aria-hidden='true' className='size-4' />
              </button>
            </div>
            {isWorking && (
              <div className='mt-5 rounded-2xl border border-royal-50/15 bg-royal-50/[0.04] p-4'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div>
                    <p className='text-sm font-semibold text-navy-220'>
                      {visibleStage
                        ? getStageLabel(visibleStage)
                        : 'Preparing your audio'}
                    </p>
                    <p className='mt-1 text-sm text-navy-220/60'>
                      Estimated time: {formatEstimate(pdfJob.fileSizeBytes)}
                    </p>
                  </div>
                  <span className='text-sm font-semibold text-royal-50'>
                    {progressPercent.toString()}%
                  </span>
                </div>
                <div className='mt-3 h-2 overflow-hidden rounded-full bg-navy-220/10'>
                  <div
                    className='h-full rounded-full bg-royal-50 transition-[width] duration-500'
                    style={{ width: `${progressPercent.toString()}%` }}
                  />
                </div>
                <p className='mt-3 text-sm leading-6 text-navy-220/60'>
                  Larger files and scanned PDFs can take a few minutes. Keep
                  this page open; after the upload finishes, the URL can recover
                  this conversion if the page refreshes.
                </p>
              </div>
            )}
          </div>
        )}

        {result && pdfJob && (
          <div className='p-6 sm:p-10'>
            {result.read_along ? (
              <PublicToolReadAlong
                audioUrl={result.tts.url}
                readAlong={result.read_along}
                speed={speed}
              />
            ) : (
              <PublicToolAudioPlayer
                key={result.tts.url}
                speed={speed}
                subtitle={`Generated with ${selectedVoice?.name ?? 'natural AI voice'}`}
                title={getFileBaseName(pdfJob.fileName)}
                url={result.tts.url}
              />
            )}
            <div className='mt-4 flex flex-wrap gap-2'>
              {clientEnv.publicToolDownloadsEnabled && (
                <button
                  className='inline-flex items-center gap-2 rounded-full border border-navy-220/15 bg-white px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5 disabled:cursor-not-allowed disabled:opacity-60'
                  disabled={download.isPending}
                  onClick={() => {
                    if (!result?.tts.url) return;
                    download.mutate({ url: result.tts.url, format, speed });
                  }}
                  type='button'
                >
                  {download.isPending ? (
                    <RefreshDouble
                      aria-hidden='true'
                      className='size-4 animate-spin'
                    />
                  ) : (
                    <Download aria-hidden='true' className='size-4' />
                  )}
                  Download {format.toUpperCase()}
                </button>
              )}
              <button
                className='inline-flex items-center gap-2 rounded-full border border-navy-220/15 bg-white px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5'
                onClick={resetTool}
                type='button'
              >
                <Refresh aria-hidden='true' className='size-4' />
                New file
              </button>
            </div>
          </div>
        )}

        {errorMessage && (
          <div
            className='flex items-start gap-2 border-t border-red-500/15 bg-red-50/60 px-6 py-4 text-sm font-medium text-red-700 sm:px-10'
            role='alert'
          >
            <WarningCircleSolid
              aria-hidden='true'
              className='mt-0.5 size-4 shrink-0'
            />
            <span>{errorMessage}</span>
          </div>
        )}

        {pdfJob && (
          <div className='flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-navy-220/10 px-6 py-5 sm:px-10'>
            {!result && (
              <VoicePicker
                disabled={voicesQuery.isLoading || generate.isPending}
                onSelect={(modelId) => {
                  setVoiceOverride(modelId);
                }}
                selectedVoice={selectedVoice}
                voices={voices}
              />
            )}
            <SpeedSelector
              disabled={generate.isPending || download.isPending}
              onChange={setSpeed}
              speed={speed}
            />
            {clientEnv.publicToolDownloadsEnabled && (
              <FormatSelector
                disabled={generate.isPending || download.isPending}
                format={format}
                onChange={setFormat}
              />
            )}
          </div>
        )}

        {pdfJob && !result && (
          <div className='border-t border-navy-220/10 p-6 sm:p-10'>
            <button
              className='flex w-full items-center justify-center gap-3 rounded-2xl bg-royal-50 px-6 py-5 text-lg font-semibold text-white transition hover:bg-navy-220 disabled:cursor-not-allowed disabled:opacity-60'
              disabled={!canGeneratePdf}
              onClick={onSubmit}
              type='button'
            >
              {generate.isPending && (
                <RefreshDouble
                  aria-hidden='true'
                  className='size-6 animate-spin'
                />
              )}
              <span>
                {generate.isPending
                  ? 'Uploading and generating audio…'
                  : 'Generate audio'}
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function VoicePicker({
  disabled,
  onSelect,
  selectedVoice,
  voices,
}: {
  disabled: boolean;
  onSelect: (modelId: Voice['model_id']) => void;
  selectedVoice: Voice | undefined;
  voices: ReadonlyArray<Voice>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Voice'
        className='group inline-flex cursor-pointer items-center gap-2 rounded-full text-sm transition focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled || voices.length === 0}
        type='button'
      >
        {selectedVoice ? (
          <>
            <img
              alt=''
              className='size-9 rounded-full object-cover ring-2 ring-white'
              height={36}
              src={selectedVoice.image_url}
              width={36}
            />
            <span className='font-medium text-navy-220'>
              {selectedVoice.name}{' '}
              <span className='text-navy-220/60'>
                · {describeVoice(selectedVoice)}
              </span>
            </span>
            <NavArrowDownSolid
              aria-hidden='true'
              className={`size-4 text-navy-220/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        ) : (
          <span className='text-navy-220/60'>{m.loading()}</span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='max-h-80 w-72 overflow-y-auto rounded-2xl border border-navy-220/10 bg-white p-2 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col gap-1' role='listbox'>
          {voices.map((voice) => {
            const isSelected = voice.model_id === selectedVoice?.model_id;
            return (
              <li key={voice.model_id}>
                <button
                  aria-selected={isSelected}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50 ${
                    isSelected ? 'bg-royal-50/5' : ''
                  }`}
                  onClick={() => {
                    onSelect(voice.model_id);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  <img
                    alt=''
                    className='size-9 rounded-full object-cover'
                    height={36}
                    src={voice.image_url}
                    width={36}
                  />
                  <span className='flex flex-col'>
                    <span className='font-medium text-navy-220'>
                      {voice.name}
                    </span>
                    <span className='text-sm text-navy-220/60'>
                      {describeVoice(voice)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function SpeedSelector({
  disabled,
  onChange,
  speed,
}: {
  disabled: boolean;
  onChange: (speed: Speed) => void;
  speed: Speed;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Speed'
        className='inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-220 transition hover:text-royal-50 disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled}
        type='button'
      >
        <span className='text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'>
          {m.tts_tool_speed_label()}
        </span>
        <span>{speed}×</span>
        <NavArrowDownSolid
          aria-hidden='true'
          className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-24 rounded-2xl border border-navy-220/10 bg-white p-1 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col' role='listbox'>
          {SPEED_OPTIONS.map((option) => {
            const isSelected = option === speed;
            return (
              <li key={option}>
                <button
                  aria-selected={isSelected}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                    isSelected ? 'font-semibold text-royal-50' : 'text-navy-220'
                  }`}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  {option}×
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function FormatSelector({
  disabled,
  format,
  onChange,
}: {
  disabled: boolean;
  format: AudioExportFormat;
  onChange: (format: AudioExportFormat) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selected = EXPORT_FORMATS.find((option) => option.value === format);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Audio format'
        className='inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-220 transition hover:text-royal-50 disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled}
        type='button'
      >
        <span className='text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'>
          Format
        </span>
        <span>{selected?.label ?? format.toUpperCase()}</span>
        <NavArrowDownSolid
          aria-hidden='true'
          className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-28 rounded-2xl border border-navy-220/10 bg-white p-1 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col' role='listbox'>
          {EXPORT_FORMATS.map((option) => {
            const isSelected = option.value === format;
            return (
              <li key={option.value}>
                <button
                  aria-selected={isSelected}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                    isSelected ? 'font-semibold text-royal-50' : 'text-navy-220'
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
